// An unfinished AI edit session, mirrored to IndexedDB so a reload can resume it.
// Every call swallows its own failure: without IndexedDB (private mode, blocked storage) the
// session runs in memory and the chat says it won't survive a reload.

import type { JSONContent } from '@tiptap/react';
import type { AiReviewFinding, AiReviewFocus } from '@/services/workflow.service';

/** One turn of the AI edit chat: what was asked, what came back, or what went wrong. */
export type AiEditMessage =
  | { id: string; role: 'user'; text: string }
  | {
      id: string;
      role: 'ai';
      summary: string | null;
      /** Pending changes and tools added right after this round landed. */
      changes: number;
      tools: number;
      /** Steps the proposal left without the values a human has to fill in. */
      incomplete: number;
    }
  /** An audit or a list of suggestions; read-only, it changed nothing. */
  | { id: string; role: 'findings'; focus: AiReviewFocus; findings: AiReviewFinding[]; partial: boolean }
  /** `retry` is the instruction to send again, `review` the review to run again; null / absent when there is nothing to retry. */
  | { id: string; role: 'error'; text: string; retry: string | null; review?: AiReviewFocus };

export interface StoredAiEditSession {
  sessionStart: JSONContent;
  base: JSONContent;
  doc: JSONContent;
  instructions: string[];
  /** The chat. Records from before the chat carry only `summary`. */
  thread?: AiEditMessage[];
  summary?: string | null;
  /** The protocol's editorDataUpdatedAt when the session started, sent back on every call. */
  baseEditorDataUpdatedAt: string | null;
  /** ISO time of the last mirror. */
  updatedAt: string;
}

const DB_NAME = 'kim-ai-edit-sessions';
const STORE = 'sessions';

export const aiEditSessionKey = (userId: string, workflowId: string) => `${userId}:${workflowId}`;

let opening: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  opening ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB unavailable'));
  }).catch((error: unknown) => {
    opening = null;
    throw error;
  });
  return opening;
}

/** One request in its own transaction; resolves with its result. */
async function run<T>(mode: IDBTransactionMode, call: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  return new Promise<T>((resolve, reject) => {
    const request = call(db.transaction(STORE, mode).objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

/** The stored session, or null when there is none or storage is unavailable. */
export async function loadAiEditSession(key: string): Promise<StoredAiEditSession | null> {
  try {
    return ((await run('readonly', (store) => store.get(key))) as StoredAiEditSession | undefined) ?? null;
  } catch {
    return null;
  }
}

/** False when the session could not be stored, so the bar can say it lives in memory only. */
export async function saveAiEditSession(key: string, session: StoredAiEditSession): Promise<boolean> {
  try {
    await run('readwrite', (store) => store.put(session, key));
    return true;
  } catch {
    return false;
  }
}

/**
 * A chat with no session behind it (reviews asked before any edit), stored on its own so it
 * survives a reload without offering a session to resume.
 */
const threadKey = (key: string) => `${key}#thread`;

export async function loadAiEditThread(key: string): Promise<AiEditMessage[] | null> {
  try {
    return ((await run('readonly', (store) => store.get(threadKey(key)))) as AiEditMessage[] | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function saveAiEditThread(key: string, thread: AiEditMessage[]): Promise<void> {
  try {
    if (thread.length) await run('readwrite', (store) => store.put(thread, threadKey(key)));
    else await run('readwrite', (store) => store.delete(threadKey(key)));
  } catch {
    // In memory only, as for sessions.
  }
}

export async function deleteAiEditSession(key: string): Promise<void> {
  try {
    await run('readwrite', (store) => store.delete(key));
  } catch {
    // Nothing stored, or nothing to store into.
  }
}

/** Logout: drop every session on this browser, whoever's it was. */
export async function clearAiEditSessions(): Promise<void> {
  try {
    await run('readwrite', (store) => store.clear());
  } catch {
    // As above.
  }
}
